import type { Theme } from '@goodtime/engine';
import { withHevcCopies } from './slotAssets';
// Veo-generated transparent celebration videos. See forestTheme.ts for pipeline notes.
/**
 * Crystal Peak — the frost-biome themed slot. Mapping:
 * cherry→snowflake, lemon→mitten, orange→bell, bell→crystal,
 * star(wild)→blizzard_wild, seven→polar_bear, gem→arctic_fox,
 * scatter→aurora_scatter, money→ice_coin.
 */
export const FROST_THEME: Theme = {
  id: 'frost',
  name: 'Crystal Peak',
  palette: {
    accent: 0x8bd1ff,         // glacier blue
    secondary: 0xc5d9f1,      // pale ice
    warm: 0xfff5e0,           // warm hearth glow
    frameFill: 0x162840,
    bgGradientTop: 0x0c1b2e,
    bgGradientBottom: 0x4f7aa8,
    dimOverlay: 0x040a14,
  },
  slot: {
    symbols: [
      { id: 'cherry',  label: 'Snowflake',   glyph: '❄️', tint: 0xc5d9f1, weight: 5,   textureAlias: 'frost:cherry',
        winVideoAlias: 'frost:cherry:win:video', winVideoSpeed: 2 },
      { id: 'lemon',   label: 'Mitten',      glyph: '🧤', tint: 0xff8a3d, weight: 5,   textureAlias: 'frost:lemon',
        winVideoAlias: 'frost:lemon:win:video', winVideoSpeed: 2 },
      { id: 'orange',  label: 'Bell',        glyph: '🔔', tint: 0xffd23b, weight: 4,   textureAlias: 'frost:orange',
        winVideoAlias: 'frost:orange:win:video', winVideoSpeed: 2 },
      { id: 'bell',    label: 'Crystal',     glyph: '💎', tint: 0x42e2f5, weight: 3,   textureAlias: 'frost:bell',
        winVideoAlias: 'frost:bell:win:video', winVideoSpeed: 2 },
      { id: 'star',    label: 'Blizzard',    glyph: '🌨️', tint: 0xeef5ff, weight: 2,   textureAlias: 'frost:star', // wild
        winVideoAlias: 'frost:star:win:video', winVideoSpeed: 2 },
      { id: 'seven',   label: 'Polar Bear',  glyph: '🐻‍❄️', tint: 0xeef5ff, weight: 1,   textureAlias: 'frost:seven',
        winVideoAlias: 'frost:seven:win:video', winVideoSpeed: 2 },
      { id: 'gem',     label: 'Arctic Fox',  glyph: '🦊', tint: 0xc8a45a, weight: 1,   textureAlias: 'frost:gem',
        winVideoAlias: 'frost:gem:win:video', winVideoSpeed: 2 },
      { id: 'scatter', label: 'Aurora',      glyph: '🌌', tint: 0x9d7be5, weight: 1,   textureAlias: 'frost:scatter',
        winVideoAlias: 'frost:scatter:win:video', winVideoSpeed: 2 },
      { id: 'money',   label: 'Ice Coin',    glyph: '$', tint: 0x8bd1ff, weight: 1.5,
        textureAlias: 'frost:money',
        winVideoAlias: 'frost:money:win:video', winVideoSpeed: 2,
        isMoney: true },
      // Gem-on-reel — producer brief 2026-06-04. Scatter-style bonus
      // shared across every biome (registered via SHARED_REEL_SYMBOLS
      // in themes/index.ts). Weights deliberately rare so a gem
      // landing feels like a treat rather than background noise.
      { id: 'gem_t5',  label: 'Gems',     glyph: '💎', tint: 0x3ea7e8, weight: 0.10, gemValue: 5,  textureAlias: 'shared:reel:gem_5' },
      { id: 'gem_t10', label: 'Gem Pile', glyph: '💎', tint: 0x3ea7e8, weight: 0.05, gemValue: 10, textureAlias: 'shared:reel:gem_10' },
    ],
    wildSymbolId: 'star',
    scatterSymbolId: 'scatter',
  },
  wheel: {
    segments: [
      { id: 'x1-a',   kind: 'multiplier', label: '×1',  tint: 0x8bd1ff, multiplier: 1,  weight: 4 },
      { id: 'x2-a',   kind: 'multiplier', label: '×2',  tint: 0x4f7aa8, multiplier: 2,  weight: 3 },
      { id: 'x0-a',   kind: 'multiplier', label: '✕',   tint: 0x162840, multiplier: 0,  weight: 2 },
      { id: 'x3-a',   kind: 'multiplier', label: '×3',  tint: 0x9d7be5, multiplier: 3,  weight: 2 },
      { id: 'x1-b',   kind: 'multiplier', label: '×1',  tint: 0x8bd1ff, multiplier: 1,  weight: 3 },
      { id: 'x5-a',   kind: 'multiplier', label: '×5',  tint: 0xfff5e0, multiplier: 5,  weight: 1 },
      { id: 'x0-b',   kind: 'multiplier', label: '✕',   tint: 0x162840, multiplier: 0,  weight: 2 },
      { id: 'x10-a',  kind: 'multiplier', label: '×10', tint: 0xfff5e0, multiplier: 10, weight: 1 },
      { id: 'x2-b',   kind: 'multiplier', label: '×2',  tint: 0x4f7aa8, multiplier: 2,  weight: 3 },
      { id: 'x1-c',   kind: 'multiplier', label: '×1',  tint: 0x8bd1ff, multiplier: 1,  weight: 3 },
      { id: 'x3-b',   kind: 'multiplier', label: '×3',  tint: 0x9d7be5, multiplier: 3,  weight: 2 },
      { id: 'jackpot',kind: 'multiplier', label: '×25', tint: 0xff5252, multiplier: 25, weight: 1 },
    ],
  },
  assets: {
    slot: {
      cherry:  { alias: 'frost:cherry',  path: 'reel/frost/snowflake.png' },
      lemon:   { alias: 'frost:lemon',   path: 'reel/frost/mitten.png' },
      orange:  { alias: 'frost:orange',  path: 'reel/frost/bell.png' },
      bell:    { alias: 'frost:bell',    path: 'reel/frost/crystal.png' },
      star:    { alias: 'frost:star',    path: 'reel/frost/blizzard_wild.png' },
      seven:   { alias: 'frost:seven',   path: 'reel/frost/polar_bear.png' },
      gem:     { alias: 'frost:gem',     path: 'reel/frost/arctic_fox.png' },
      scatter: { alias: 'frost:scatter', path: 'reel/frost/aurora_scatter.png' },
      money:   { alias: 'frost:money',   path: 'reel/frost/ice_coin.png' },
    },
    slotWin: withHevcCopies({
      cherry:  { alias: 'frost:cherry:win:video',  path: 'reel/frost/snowflake_win.webm' },
      lemon:   { alias: 'frost:lemon:win:video',   path: 'reel/frost/mitten_win.webm' },
      orange:  { alias: 'frost:orange:win:video',  path: 'reel/frost/bell_win.webm' },
      bell:    { alias: 'frost:bell:win:video',    path: 'reel/frost/crystal_win.webm' },
      star:    { alias: 'frost:star:win:video',    path: 'reel/frost/blizzard_wild_win.webm' },
      seven:   { alias: 'frost:seven:win:video',   path: 'reel/frost/polar_bear_win.webm' },
      gem:     { alias: 'frost:gem:win:video',     path: 'reel/frost/arctic_fox_win.webm' },
      scatter: { alias: 'frost:scatter:win:video', path: 'reel/frost/aurora_scatter_win.webm' },
      money:   { alias: 'frost:money:win:video',   path: 'reel/frost/ice_coin_win.webm' },
    }),
    scene: {
      background:       { alias: 'frost:bg',         path: 'environment/frost.png' },
      columnBackground: { alias: 'frost:column_bg',  path: 'reel/frost_column_bg.png' },
      border:           { alias: 'frost:border',     path: 'brand/border_frost.png' },
    },
  },
};
